"use client";

import type { ProductVariant } from "@/src/types/product";

interface Props {
  variants: ProductVariant[];
  selectedVariantId: string | null;
  onSelect: (variantId: string) => void;
}

/**
 * Pill-row selector for product variants (size / weight / flavor, etc.).
 * Out-of-stock variants render disabled and struck-through. No variant is
 * pre-selected — the shopper must pick one before adding to cart.
 */
export function VariantSelector({ variants, selectedVariantId, onSelect }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[12px] font-bold uppercase tracking-wider text-[#8A8A85]">
        Choose an option
      </p>
      <div className="flex flex-wrap gap-2">
        {variants.map((v) => {
          const selected = v.variantId === selectedVariantId;
          const outOfStock = (v.stockQuantity ?? 0) <= 0;
          return (
            <button
              key={v.variantId}
              type="button"
              disabled={outOfStock}
              aria-pressed={selected}
              onClick={() => onSelect(v.variantId)}
              className="rounded-xl border-2 px-4 py-2 text-[13px] font-semibold transition disabled:cursor-not-allowed"
              style={{
                borderColor: selected ? "#F26250" : outOfStock ? "#EAE7E0" : "#E8E3DA",
                background: selected ? "#FEF6F4" : outOfStock ? "#F4F3F0" : "#fff",
                color: outOfStock ? "#A8A39B" : selected ? "#D94A39" : "#1D1D1D",
                opacity: outOfStock ? 0.55 : 1,
                filter: outOfStock ? "grayscale(1)" : "none",
              }}
            >
              {v.label}
              {outOfStock && (
                <span className="ml-1.5 text-[10px] font-medium">· Sold out</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
